import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Label,
  Message,
  Modal,
  Progress,
  Segment,
  Step,
  Table,
} from 'semantic-ui-react';
import { stockyApi } from '../misc/StockyApi';
import EditableNumberInput from './importWizard/EditableNumberInput';
import ImportPreviewTable from './importWizard/ImportPreviewTable';
import ImportWizardModal from './importWizard/ImportWizardModal';
import { formatAmount, formatCurrency as formatSharedCurrency, parseLocalizedAmount } from '../misc/PriceFormatter';

const STEP_ACCOUNT = 0;
const STEP_MODE = 1;
const STEP_TEMPLATE = 2;
const STEP_VALIDATION = 3;
const STEP_EXECUTION = 4;

const IMPORT_MODE_COSTS = 'COSTS';
const IMPORT_MODE_PRICES = 'PRICES';

const LISTING_STATUS_LABELS = {
  active: 'Activa',
  paused: 'Pausada',
  closed: 'Cerrada',
  under_review: 'En revisión',
  inactive: 'Inactiva',
  payment_required: 'Pago requerido',
  pending: 'Pendiente',
  not_yet_active: 'Aún no activa',
  programmed: 'Programada',
  unknown: 'Desconocida',
};
const LISTING_STATUS_OPTIONS = Object.keys(LISTING_STATUS_LABELS).map((value) => ({
  key: value,
  value,
  text: LISTING_STATUS_LABELS[value],
}));

const parseSkuFilters = (value) => Array.from(new Map(
  String(value || '')
    .split(/[\s,]+/)
    .map((sku) => sku.trim())
    .filter(Boolean)
    .map((sku) => [sku.toLocaleLowerCase(), sku]),
).values());

const formatCurrency = (value) => formatSharedCurrency(Number(value || 0), { stripZeroDecimals: true });

const resolveRowStatusColor = (status, rowErrors = []) => {
  if (status === 'VALID') return 'green';
  if (status === 'SKIPPED' && rowErrors.length > 0) return 'orange';
  if (status === 'ERROR') return 'red';
  return 'grey';
};

const buildPendingImportState = (importId, previewSummary) => ({
  importId,
  status: 'RUNNING',
  totalRows: Number(previewSummary?.validRows || 0),
  processedRows: 0,
  costUpdatedItems: 0,
  priceUpdatedRows: 0,
  failedRows: 0,
  results: [],
});

const formatProgressText = (processedRows, totalRows) => {
  const processed = Number(processedRows || 0);
  const total = Number(totalRows || 0);
  if (!total) return '0 / 0';
  return `${processed} / ${total}`;
};

const helperArrowStyle = {
  color: '#2185d0',
  fontSize: 22,
  lineHeight: 1,
  fontWeight: 700,
};

const BulkPricingImportWizardModal = ({
  open,
  onClose,
  authUser,
  onCompleted,
}) => {
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedMode, setSelectedMode] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importState, setImportState] = useState(null);
  const [activeStep, setActiveStep] = useState(STEP_ACCOUNT);
  const [error, setError] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');
  const [templateRowCount, setTemplateRowCount] = useState(null);
  const [templateCountLoading, setTemplateCountLoading] = useState(false);
  const [skuFilterInput, setSkuFilterInput] = useState('');
  const [listingStatuses, setListingStatuses] = useState([]);
  const [minCurrentValue, setMinCurrentValue] = useState('');
  const [maxCurrentValue, setMaxCurrentValue] = useState('');

  const previewSummary = previewData?.summary || null;
  const importProgress = importState || null;

  const progressPercent = useMemo(() => {
    const total = Number(importProgress?.totalRows || 0);
    const processed = Number(importProgress?.processedRows || 0);
    if (!total) return 0;
    return Math.min(100, Math.round((processed / total) * 100));
  }, [importProgress]);

  useEffect(() => {
    if (!open || !authUser) {
      return undefined;
    }

    let cancelled = false;
    setAccountsLoading(true);
    setAccounts([]);
    setSelectedAccounts([]);
    setSelectedMode('');
    setFileName('');
    setFileContent('');
    setPreviewData(null);
    setImportState(null);
    setActiveStep(STEP_ACCOUNT);
    setError('');
    setTemplateMessage('');
    setTemplateRowCount(null);
    setSkuFilterInput('');
    setListingStatuses([]);
    setMinCurrentValue('');
    setMaxCurrentValue('');

    stockyApi
      .getPricingAccounts(authUser)
      .then((response) => {
        if (cancelled) return;
        setAccounts(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError?.response?.data?.message || 'No se pudieron cargar las cuentas sincronizadas.');
      })
      .finally(() => {
        if (!cancelled) {
          setAccountsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, authUser]);

  const templateFilters = useMemo(() => ({
    skus: parseSkuFilters(skuFilterInput),
    ...(listingStatuses.length > 0 ? { listingStatuses } : {}),
    ...(minCurrentValue !== '' ? { minCurrentValue: Number(minCurrentValue) } : {}),
    ...(maxCurrentValue !== '' ? { maxCurrentValue: Number(maxCurrentValue) } : {}),
  }), [skuFilterInput, listingStatuses, minCurrentValue, maxCurrentValue]);

  useEffect(() => {
    if (!open || !authUser || activeStep !== STEP_TEMPLATE || !selectedMode || selectedAccounts.length === 0) {
      return undefined;
    }
    let cancelled = false;
    setTemplateCountLoading(true);
    stockyApi
      .getPricingImportTemplateSummary(
        selectedAccounts.map((account) => account.sellerId),
        selectedMode,
        templateFilters,
        authUser,
      )
      .then((response) => {
        if (!cancelled) setTemplateRowCount(Number(response?.data?.rowCount || 0));
      })
      .catch(() => {
        if (!cancelled) setTemplateRowCount(null);
      })
      .finally(() => {
        if (!cancelled) setTemplateCountLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, authUser, activeStep, selectedMode, selectedAccounts, templateFilters]);

  useEffect(() => {
    if (!open || !authUser || !importProgress?.importId) {
      return undefined;
    }
    if (importProgress.status === 'COMPLETED' || importProgress.status === 'FAILED') {
      return undefined;
    }

    let cancelled = false;

    const syncStatus = () => {
      stockyApi
        .getPricingImportStatus(importProgress.importId, authUser)
        .then((response) => {
          if (cancelled) return;
          const nextState = response?.data || null;
          setImportState(nextState);
          setExecutionLoading(false);
          if (nextState?.status === 'COMPLETED' && typeof onCompleted === 'function') {
            onCompleted(nextState);
          }
        })
        .catch((requestError) => {
          if (cancelled) return;
          setExecutionLoading(false);
          setError(requestError?.response?.data?.message || 'No se pudo obtener el progreso de la importacion.');
        });
    };

    syncStatus();
    const interval = setInterval(syncStatus, 1500);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, authUser, importProgress?.importId, importProgress?.status, onCompleted]);

  const handleSelectAccount = (account) => {
    setSelectedAccounts((current) => {
      const key = `${account.marketplace}:${account.sellerId}`;
      return current.some((entry) => `${entry.marketplace}:${entry.sellerId}` === key)
        ? current.filter((entry) => `${entry.marketplace}:${entry.sellerId}` !== key)
        : [...current, account];
    });
    setError('');
    setTemplateMessage('');
    setTemplateRowCount(null);
  };

  const handleSelectMode = (mode) => {
    setSelectedMode(mode);
    setFileName('');
    setFileContent('');
    setPreviewData(null);
    setImportState(null);
    setError('');
    setTemplateMessage('');
    setActiveStep(STEP_TEMPLATE);
  };

  const handleDownloadTemplate = async () => {
    if (selectedAccounts.length === 0 || !selectedMode || !authUser) return;

    setError('');
    setTemplateMessage('');
    setDownloadLoading(true);

    try {
      const response = await stockyApi.downloadPricingImportTemplate(
        selectedAccounts.map((account) => account.sellerId),
        selectedMode,
        templateFilters,
        authUser,
      );
      const dispositionHeader = response?.headers?.['content-disposition'] || '';
      const fileNameMatch = /filename=\"?([^\";]+)\"?/i.exec(dispositionHeader);
      const downloadName =
        fileNameMatch?.[1] || `pricing-template-${String(selectedMode || '').toLowerCase()}-multi-account.csv`;

      const blob = new Blob([response.data], {
        type: response?.headers?.['content-type'] || 'text/csv;charset=utf-8;',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const rowCount = Number(response?.headers?.['x-export-row-count']);
      setTemplateMessage(`Plantilla descargada: ${downloadName}. Incluye ${Number.isFinite(rowCount) ? rowCount : templateRowCount || 0} filas.`);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo descargar la plantilla.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setFileName(file.name);
      setFileContent(text);
      setPreviewData(null);
      setImportState(null);
      setError('');
    } catch (readError) {
      setError(readError instanceof Error ? readError.message : 'No se pudo leer el archivo.');
    }
  };

  const handlePreview = async () => {
    if (selectedAccounts.length === 0 || !selectedMode || !fileContent || !authUser) return;

    setPreviewLoading(true);
    setError('');

    try {
      const response = await stockyApi.previewPricingImport(
        {
          sellerId: selectedAccounts[0].sellerId,
          sellerIds: selectedAccounts.map((account) => account.sellerId),
          importMode: selectedMode,
          fileName,
          fileContent,
        },
        authUser,
      );
      setPreviewData(response?.data || null);
      setActiveStep(STEP_VALIDATION);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo previsualizar la importacion.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePriceEdit = async (row, nextPrice) => {
    if (!previewData?.draftId || !authUser) return;
    setError('');
    try {
      const response = await stockyApi.updatePricingImportPrices(
        previewData.draftId,
        [{ rowNumber: row.rowNumber, nextPrice }],
        authUser,
      );
      setPreviewData((current) => ({ ...current, ...response.data }));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo actualizar el precio.');
    }
  };

  const handleExecute = async () => {
    if (!previewData?.draftId || !authUser) return;

    setExecutionLoading(true);
    setError('');
    setActiveStep(STEP_EXECUTION);

    try {
      const response = await stockyApi.executePricingImport(
        {
          draftId: previewData.draftId,
        },
        authUser,
      );
      const importId = response?.data?.importId;
      setImportState(buildPendingImportState(importId, previewSummary));
    } catch (requestError) {
      setExecutionLoading(false);
      setError(requestError?.response?.data?.message || 'No se pudo ejecutar la importacion.');
    }
  };

  const previewHasBlockingErrors = Number(previewSummary?.errorRows || 0) > 0;
  const previewHasValidRows = Number(previewSummary?.validRows || 0) > 0;
  const isCostsMode = selectedMode === IMPORT_MODE_COSTS;
  const isPricesMode = selectedMode === IMPORT_MODE_PRICES;

const renderStepHeader = () => (
    <Step.Group ordered fluid stackable="tablet" size="mini" style={{ marginBottom: 16 }}>
      <Step active={activeStep === STEP_ACCOUNT} completed={activeStep > STEP_ACCOUNT}>
        <Step.Content>
          <Step.Title>Cuenta</Step.Title>
          <Step.Description>Elegi las cuentas a impactar</Step.Description>
        </Step.Content>
      </Step>
      <Step active={activeStep === STEP_MODE} completed={activeStep > STEP_MODE}>
        <Step.Content>
          <Step.Title>Tipo</Step.Title>
          <Step.Description>Define si vas a cargar costos o precios</Step.Description>
        </Step.Content>
      </Step>
      <Step active={activeStep === STEP_TEMPLATE} completed={activeStep > STEP_TEMPLATE}>
        <Step.Content>
          <Step.Title>Plantilla</Step.Title>
          <Step.Description>Descarga opcional y carga del CSV</Step.Description>
        </Step.Content>
      </Step>
      <Step active={activeStep === STEP_VALIDATION} completed={activeStep > STEP_VALIDATION}>
        <Step.Content>
          <Step.Title>Validacion</Step.Title>
          <Step.Description>Revisa los cambios detectados</Step.Description>
        </Step.Content>
      </Step>
      <Step active={activeStep === STEP_EXECUTION}>
        <Step.Content>
          <Step.Title>Ejecucion</Step.Title>
          <Step.Description>Seguimiento y resultado final</Step.Description>
        </Step.Content>
      </Step>
    </Step.Group>
  );

  const renderAccountSummary = () => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'grid', gap: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 24 }}>
          {selectedAccounts.length === 1
            ? selectedAccounts[0]?.nickname || selectedAccounts[0]?.sellerId
            : `${selectedAccounts.length} cuentas seleccionadas`}
        </div>
      <div style={{ color: '#666' }}>
        {selectedAccounts.length === 1
          ? `${selectedAccounts[0]?.marketplace || '-'} · Seller ${selectedAccounts[0]?.sellerId || '-'} · ${selectedAccounts[0]?.listingCount || 0} publicaciones`
          : `${selectedAccounts.reduce((total, account) => total + Number(account.listingCount || 0), 0)} publicaciones en las cuentas elegidas`}
      </div>
      {selectedMode ? (
        <div style={{ color: '#666' }}>
          Tipo: {selectedMode === IMPORT_MODE_COSTS ? 'Actualizar costos' : 'Actualizar precios'}
        </div>
      ) : null}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {activeStep !== STEP_ACCOUNT ? (
          <Button basic type="button" onClick={() => setActiveStep(STEP_ACCOUNT)} style={{ minHeight: 48, padding: '0 22px' }}>
            Volver a cuentas
          </Button>
        ) : null}
      </div>
    </div>
  );

  const renderAccountStep = () => (
    <Segment loading={accountsLoading} style={{ flex: 1, padding: 24, borderRadius: 14, overflow: 'auto' }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Selecciona una o varias cuentas sincronizadas</div>
      <div style={{ color: '#666', marginBottom: 14 }}>Podés combinar cuentas de Mercado Libre y Tienda Nube. Para precios, la plantilla multi-cuenta incluye seller_id para identificar cada publicación.</div>
      {accounts.length === 0 && !accountsLoading ? (
        <Message info content="No hay cuentas sincronizadas para importar." />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 14,
          }}
        >
          {accounts.map((account) => (
            <button
              key={`${account.marketplace}-${account.sellerId}`}
              type="button"
              onClick={() => handleSelectAccount(account)}
              style={{
                textAlign: 'left',
                border: selectedAccounts.some((entry) => `${entry.marketplace}:${entry.sellerId}` === `${account.marketplace}:${account.sellerId}`)
                  ? '2px solid #2185d0'
                  : '1px solid #d4d4d5',
                borderRadius: 12,
                padding: 18,
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 22 }}>
                {account.nickname || account.sellerId}
              </div>
              <div style={{ color: '#666', marginTop: 6 }}>
                {account.marketplace} · ID {account.sellerId}
              </div>
              <Label basic color="blue" style={{ marginTop: 12 }}>
                {account.listingCount} publicaciones
              </Label>
              {Number(account.listingCount || 0) === 0 ? (
                <div style={{ color: '#666', marginTop: 10, fontSize: 12 }}>
                  Cuenta sincronizada sin publicaciones locales todavia.
                </div>
              ) : null}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
        <Button
          color="purple"
          type="button"
          disabled={selectedAccounts.length === 0}
          onClick={() => setActiveStep(STEP_MODE)}
        >
          Continuar con {selectedAccounts.length || 0} cuentas
        </Button>
      </div>
    </Segment>
  );

  const renderModeStep = () => (
    <Segment style={{ flex: 1, padding: 24, borderRadius: 14, overflow: 'auto' }}>
      {renderAccountSummary()}
      <div style={{ fontWeight: 700, marginBottom: 14, marginTop: 18 }}>Selecciona el tipo de actualizacion</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 14,
        }}
      >
        {[
          {
            mode: IMPORT_MODE_COSTS,
            title: 'Actualizar costos',
            description: 'Impacta item.cost por SKU unico y luego te muestra las publicaciones alcanzadas en la validacion.',
          },
          {
            mode: IMPORT_MODE_PRICES,
            title: 'Actualizar precios',
            description: 'Impacta precios de publicaciones especificas de las cuentas seleccionadas.',
          },
        ].map((option) => (
          <button
            key={option.mode}
            type="button"
            onClick={() => handleSelectMode(option.mode)}
            style={{
              textAlign: 'left',
              border: '1px solid #d4d4d5',
              borderRadius: 12,
              padding: 20,
              background: '#fff',
              cursor: 'pointer',
              display: 'grid',
              gap: 10,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 22 }}>{option.title}</div>
            <div style={{ color: '#666', lineHeight: 1.5 }}>{option.description}</div>
          </button>
        ))}
      </div>
    </Segment>
  );

  const renderTemplateStep = () => (
    <Segment style={{ flex: 1, padding: 24, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 20 }}>
      {renderAccountSummary()}
      <div
        style={{
          border: '1px solid #dcdcdc',
          borderRadius: 12,
          padding: 20,
          background: '#fcfcfc',
          display: 'grid',
          gap: 14,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 18 }}>Descarga la planilla o sube tu CSV</div>
        <div style={{ display: 'grid', gap: 18 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              gap: 16,
              alignItems: 'center',
            }}
          >
            <div
              style={{
                border: '1px solid #b7d9f6',
                background: '#f4fbff',
                borderRadius: 12,
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 64,
              }}
            >
              <span style={{ color: '#0e5a8a', lineHeight: 1.45 }}>
                {isCostsMode
                  ? 'Puedes descargar la base o subir un CSV que ya tengas. Para costos se usa SKU y veras en la validacion las publicaciones impactadas.'
                  : 'Puedes descargar la base o subir un CSV que ya tengas. Para precios se usa listing_id y marketplace_id; en una plantilla multi-cuenta también se incluye seller_id.'}
              </span>
              <span aria-hidden="true" style={helperArrowStyle}>→</span>
            </div>
            <Button
              color="blue"
              type="button"
              onClick={handleDownloadTemplate}
              loading={downloadLoading}
              disabled={selectedAccounts.length === 0 || downloadLoading}
              style={{ minHeight: 52, padding: '0 26px', margin: 0, whiteSpace: 'nowrap' }}
            >
              <Icon name="download" />
              Descargar plantilla
            </Button>
          </div>
          <Message info style={{ margin: 0 }}>
            <Message.Header>Alcance de la plantilla</Message.Header>
            <p style={{ margin: '6px 0 0' }}>
              {selectedAccounts.length} {selectedAccounts.length === 1 ? 'cuenta seleccionada' : 'cuentas seleccionadas'} · {selectedMode === IMPORT_MODE_COSTS ? 'Costos' : 'Precios'}
            </p>
            <p style={{ margin: '6px 0 0' }}>
              {templateFilters.skus.length > 0 ? `${templateFilters.skus.length} SKU indicados` : 'Todos los SKU'} · {listingStatuses.length > 0 ? `${listingStatuses.length} estados` : 'Cualquier estado'}
            </p>
            <p style={{ margin: '6px 0 0' }}>
              {templateCountLoading
                ? 'Calculando la descarga…'
                : templateRowCount === null
                  ? 'La cantidad se confirmará al descargar.'
                  : `La descarga incluirá ${templateRowCount} ${isCostsMode ? 'SKU' : 'publicaciones'}.`}
            </p>
          </Message>
          <div style={{ display: 'grid', gap: 12, padding: 16, border: '1px solid #dce8f8', borderRadius: 10, background: '#f8fbff' }}>
            <div style={{ fontWeight: 700 }}>Filtros de la descarga</div>
            <textarea
              aria-label="SKU para costos y precios"
              rows={2}
              value={skuFilterInput}
              onChange={(event) => setSkuFilterInput(event.target.value)}
              placeholder="SKU separados por coma, espacios o saltos de línea"
              style={{ width: '100%', resize: 'vertical', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <Dropdown
                fluid
                multiple
                search
                selection
                clearable
                placeholder="Cualquier estado"
                options={LISTING_STATUS_OPTIONS}
                value={listingStatuses}
                onChange={(_, data) => setListingStatuses(Array.isArray(data.value) ? data.value : [])}
              />
              <input type="number" min="0" step="0.01" value={minCurrentValue} onChange={(event) => setMinCurrentValue(event.target.value)} placeholder={`Mínimo ${isCostsMode ? 'costo' : 'precio'}`} style={{ padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }} />
              <input type="number" min="0" step="0.01" value={maxCurrentValue} onChange={(event) => setMaxCurrentValue(event.target.value)} placeholder={`Máximo ${isCostsMode ? 'costo' : 'precio'}`} style={{ padding: 10, border: '1px solid #cbd5e1', borderRadius: 6 }} />
            </div>
          </div>
          <div style={{ display: 'grid', gap: 12 }}>
            <div
              style={{
                border: '1px solid #dcdcdc',
                borderRadius: 12,
                padding: 20,
                background: '#fff',
                display: 'grid',
                gap: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ color: '#555', lineHeight: 1.45 }}>
                  Si ya tienes la planilla, súbela directamente y continúa con la validación.
                </span>
                <span aria-hidden="true" style={helperArrowStyle}>→</span>
              </div>
              <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
              {fileName ? (
                <Message
                  positive
                  content={`Archivo listo: ${fileName}. Se buscaran items y publicaciones de la cuenta seleccionada por SKU.`}
                  style={{ margin: 0 }}
                />
              ) : null}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            color="purple"
            type="button"
            onClick={handlePreview}
            loading={previewLoading}
              disabled={selectedAccounts.length === 0 || !fileContent || previewLoading}
            style={{ minHeight: 48, padding: '0 24px' }}
          >
            Continuar con validacion
          </Button>
        </div>
      </div>
    </Segment>
  );

  const validationColumns = [
    { key: 'rowNumber', label: 'Fila', render: (row) => row.rowNumber },
    { key: 'sku', label: 'SKU', render: (row) => row.sku || '-' },
    {
      key: 'listing',
      label: isCostsMode ? 'Item / publicaciones impactadas' : 'Publicacion',
      render: (row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.listingTitle || row.itemTitle || '-'}</div>
          {isCostsMode && Array.isArray(row.impactedListings) && row.impactedListings.length > 0 ? (
            <div style={{ color: '#666', marginTop: 4, display: 'grid', gap: 2 }}>
              {row.impactedListings.map((entry) => (
                <div key={`${row.rowNumber}-${entry.listingId}`}>
                  {entry.marketplaceId} · {entry.listingTitle || 'Publicacion'}
                </div>
              ))}
            </div>
          ) : null}
          {row.errors?.length > 0 ? (
            <div style={{ color: '#a33333', marginTop: 4 }}>{row.errors.join(' - ')}</div>
          ) : null}
        </>
      ),
    },
    ...(isCostsMode
      ? [{
          key: 'cost',
          label: 'Costo',
          render: (row) => `${formatCurrency(row.currentCost)} -> ${row.nextCost === null ? '-' : formatCurrency(row.nextCost)}`,
        }]
      : []),
    ...(isPricesMode
      ? [{
          key: 'price',
          label: 'Precio',
          width: '1%',
          render: (row) => (
            <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {formatCurrency(row.currentPrice)} {'->'}
              {row.listingId ? (
                <EditableNumberInput
                  value={row.nextPrice}
                  format={formatAmount}
                  parse={parseLocalizedAmount}
                  prefix="$"
                  placeholder="Nuevo precio"
                  ariaLabel={`Nuevo precio fila ${row.rowNumber}`}
                  onCommit={(nextPrice) => handlePriceEdit(row, nextPrice)}
                />
              ) : '-'}
            </span>
          ),
        }]
      : []),
    {
      key: 'status',
      label: 'Estado',
      render: (row) => <Label color={resolveRowStatusColor(row.status, row.errors)}>{row.status}</Label>,
    },
  ];

  const renderValidationStep = () => (
    <Segment style={{ flex: 1, padding: 24, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
      {renderAccountSummary()}
      {templateMessage ? <Message positive content={templateMessage} /> : null}
      {previewData ? (
        <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Label color="blue">Filas: {previewSummary?.totalRows || 0}</Label>
        <Label color="green">Validas: {previewSummary?.validRows || 0}</Label>
        <Label>Sin cambios: {previewSummary?.skippedRows || 0}</Label>
        <Label color="orange">Omitidas: {previewSummary?.skippedRows || 0}</Label>
        <Label color="teal">Costos: {previewSummary?.costUpdates || 0}</Label>
        <Label color="teal">Precios: {previewSummary?.priceUpdates || 0}</Label>
      </div>
      {previewSummary?.skippedRows > 0 ? (
        <Message warning content="Las filas con datos invalidos o ambiguos se omitiran automaticamente. Solo se importara lo correcto." />
      ) : null}
      {!previewHasValidRows ? (
        <Message info content="No hay cambios validos para ejecutar." />
      ) : null}
      <ImportPreviewTable
        rows={previewData?.rows || []}
        getRowId={(row) => `${row.rowNumber}-${row.listingId || row.sku}`}
        columns={validationColumns}
      />
        </>
      ) : null}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 'auto' }}>
        <Button basic type="button" onClick={() => setActiveStep(STEP_TEMPLATE)}>
          Volver a la planilla
        </Button>
        <Button
          color="green"
          type="button"
          onClick={handleExecute}
          loading={executionLoading}
          disabled={!previewData || !previewHasValidRows || previewHasBlockingErrors || executionLoading}
        >
          Ejecutar importacion
        </Button>
      </div>
    </Segment>
  );

  const resultColumns = [
    { key: 'rowNumber', label: 'Fila', render: (row) => row.rowNumber },
    { key: 'sku', label: 'SKU', render: (row) => row.sku || '-' },
    {
      key: 'listing',
      label: 'Publicacion',
      render: (row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.listingTitle || row.itemTitle || '-'}</div>
          {row.importMode === IMPORT_MODE_COSTS && Array.isArray(row.impactedListings) && row.impactedListings.length > 0 ? (
            <div style={{ color: '#666', marginTop: 4, display: 'grid', gap: 2 }}>
              {row.impactedListings.map((entry) => (
                <div key={`${row.rowNumber}-${entry.listingId}`}>
                  {entry.marketplaceId} · {entry.listingTitle || 'Publicacion'}
                </div>
              ))}
            </div>
          ) : null}
          {row.errors?.length > 0 ? (
            <div style={{ color: '#a33333', marginTop: 4 }}>{row.errors.join(' - ')}</div>
          ) : null}
        </>
      ),
    },
    ...(isCostsMode
      ? [{
          key: 'cost',
          label: 'Costo',
          render: (row) => (
            <>
              <Label color={row.costStatus === 'FAILED' ? 'red' : row.costStatus === 'UPDATED' ? 'green' : undefined}>
                {row.costStatus}
              </Label>
              <div style={{ marginTop: 4 }}>{row.nextCost === null ? '-' : formatCurrency(row.nextCost)}</div>
            </>
          ),
        }]
      : []),
    ...(isPricesMode
      ? [{
          key: 'price',
          label: 'Precio',
          render: (row) => (
            <>
              <Label color={row.priceStatus === 'FAILED' ? 'red' : row.priceStatus === 'UPDATED' ? 'green' : undefined}>
                {row.priceStatus}
              </Label>
              <div style={{ marginTop: 4 }}>{row.nextPrice === null ? '-' : formatCurrency(row.nextPrice)}</div>
            </>
          ),
        }]
      : []),
  ];

  const renderExecutionStep = () => {
    const isRunning = importProgress?.status === 'RUNNING' || executionLoading;
    const hasFinished = importProgress?.status === 'COMPLETED' || importProgress?.status === 'FAILED';
    const hasResults = Array.isArray(importProgress?.results) && importProgress.results.length > 0;

    return (
      <Segment style={{ flex: 1, padding: 24, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
        {renderAccountSummary()}
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {isRunning ? 'Procesando importacion' : 'Resultado final'}
          </div>
          <div style={{ color: '#666' }}>
            {isRunning
              ? 'El backend esta impactando los cambios. Los contadores se actualizan automaticamente.'
              : 'Revisa el detalle fila por fila del procesamiento.'}
          </div>
        </div>
        <Progress
          percent={progressPercent}
          indicating={isRunning}
          success={importProgress?.status === 'COMPLETED'}
          error={importProgress?.status === 'FAILED'}
          style={{ marginBottom: 0 }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#666', fontSize: 13 }}>
          <span>Avance</span>
          <span>{formatProgressText(importProgress?.processedRows, importProgress?.totalRows)}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Label color="blue">Procesadas: {importProgress?.processedRows || 0}/{importProgress?.totalRows || 0}</Label>
          <Label color="green">Costos impactados: {importProgress?.costUpdatedItems || 0}</Label>
          <Label color="green">Precios impactados: {importProgress?.priceUpdatedRows || 0}</Label>
          <Label color={(importProgress?.failedRows || 0) > 0 ? 'red' : undefined}>Filas con error: {importProgress?.failedRows || 0}</Label>
        </div>
        {executionLoading && (!importProgress || importProgress.processedRows === 0) ? (
          <Message info icon>
            <Icon name="spinner" loading />
            <Message.Content>
              <Message.Header>Iniciando importacion</Message.Header>
              En cuanto el backend confirme el primer lote, veras avanzar el contador.
            </Message.Content>
          </Message>
        ) : null}
        {importProgress?.error ? <Message negative content={importProgress.error} /> : null}
        {hasFinished && !hasResults ? (
          <Message warning content="La importacion finalizo pero no devolvio filas procesadas. Revisa si la previsualizacion tenia filas validas." />
        ) : null}
        <ImportPreviewTable
          rows={importProgress?.results || []}
          getRowId={(row) => `${row.rowNumber}-${row.listingId || row.sku}`}
          columns={resultColumns}
        />
      </Segment>
    );
  };

  const renderCurrentStep = () => {
    if (activeStep === STEP_ACCOUNT) return renderAccountStep();
    if (activeStep === STEP_MODE) return renderModeStep();
    if (activeStep === STEP_TEMPLATE) return renderTemplateStep();
    if (activeStep === STEP_VALIDATION) return renderValidationStep();
    return renderExecutionStep();
  };

  return (
    <ImportWizardModal
      open={open}
      title="Importar costos y precios"
      onClose={onClose}
      stepHeader={renderStepHeader()}
      error={error}
    >
      {renderCurrentStep()}
    </ImportWizardModal>
  );
};

export default BulkPricingImportWizardModal;
